import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { X } from "@phosphor-icons/react";
import { Formik } from "formik";
import * as Yup from "yup";

import type { Goal } from "api/types/aba";
import useCreateGoal from "hooks/api/goals/useCreateGoal";
import useUpdateGoal from "hooks/api/goals/useUpdateGoal";

import GoalFormSection1 from "./GoalFormSection1";
import GoalFormSection15 from "./GoalFormSection15";
import GoalFormSection2 from "./GoalFormSection2";
import type { GoalFormValues } from "./GoalBuilderPanel.types";
import { GOAL_FORM_INITIAL_VALUES } from "./GoalBuilderPanel.types";
import ConfirmDialog from "../../shared/ConfirmDialog";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Default rationale attached to PUT payloads when the user changes `status`
 * from the edit form. The form intentionally does not expose a rationale
 */
const STATUS_CHANGE_RATIONALE_DEFAULT = "Updated via goal edit form";

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const validationSchema = Yup.object({
  behavior_subject: Yup.string().required("Required"),
  goal_type: Yup.string().required("Required"),
  title: Yup.string().trim().required("Goal name is required"),
  domain: Yup.string().required("Required"),
  status: Yup.string().required("Required"),
  description: Yup.string().trim().required("Goal statement is required"),
  // Section 1.5 — conditional on behavior-reduction goal type
  operational_definition: Yup.string().when("goal_type", {
    is: "behavior-reduction",
    then: (s) => s.trim().required("Operational definition is required"),
    otherwise: (s) => s,
  }),
  hypothesized_functions: Yup.array().when("goal_type", {
    is: "behavior-reduction",
    then: (s) => s.min(1, "At least one hypothesized function is required"),
    otherwise: (s) => s,
  }),
  common_antecedents: Yup.string().when("goal_type", {
    is: "behavior-reduction",
    then: (s) => s.trim().required("Common antecedents is required"),
    otherwise: (s) => s,
  }),
  mastery_definition: Yup.string().trim().required("Mastery definition is required"),
  generalization_definition: Yup.string().trim().required("Generalization definition is required"),
});

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface GoalBuilderPanelProps {
  /** Controls dialog visibility */
  open: boolean;
  /** Called when the dialog should close */
  onClose: () => void;
  /** "create" opens a blank form; "edit" pre-populates from `goal` */
  mode: "create" | "edit";
  /** Client ID — required for create (POST) and query invalidation */
  clientId: string;
  /** Goal to pre-populate in edit mode */
  goal?: Goal;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function goalToFormValues(goal: Goal): GoalFormValues {
  return {
    behavior_subject: goal.behavior_subject ?? "client",
    goal_type: goal.goal_type ?? "skill-acquisition",
    title: goal.title ?? "",
    domain: goal.domain ?? "",
    status: goal.status ?? "not-active",
    description: goal.description ?? "",
    general_session_instructions: goal.general_session_instructions ?? "",
    reinforcement_context: goal.reinforcement_context ?? "",
    strategy_notes: goal.strategy_notes ?? "",
    operational_definition: goal.operational_definition ?? "",
    hypothesized_functions: goal.hypothesized_functions ?? [],
    common_antecedents: goal.common_antecedents ?? "",
    mastery_definition: goal.mastery_definition ?? "",
    generalization_definition: goal.generalization_definition ?? "",
    resources: goal.resources ?? [],
  };
}

// ---------------------------------------------------------------------------
// GoalBuilderPanel
// ---------------------------------------------------------------------------

/**
 * Dialog for creating or editing a goal.
 * Renders Section 1 (goal info), Section 1.5 (behavior reduction — conditional),
 * and Section 2 (mastery & generalization). Owns its own mutation logic.
 *
 * @param props
 * @param props.open     - Dialog visibility
 * @param props.onClose  - Close handler (called on success, cancel, or X click)
 * @param props.mode     - "create" for new goals, "edit" to update an existing one
 * @param props.clientId - Client ID for the POST endpoint and query invalidation
 * @param props.goal     - Populated in edit mode; ignored in create mode
 */
const GoalBuilderPanel: React.FC<GoalBuilderPanelProps> = ({
  open,
  onClose,
  mode,
  clientId,
  goal,
}) => {
  const isEdit = mode === "edit";
  const [discardOpen, setDiscardOpen] = useState(false);

  const { createGoalAction, isCreatingGoal } = useCreateGoal({ onSuccess: onClose });
  const { updateGoalAction, isUpdatingGoal } = useUpdateGoal({ onSuccess: onClose });

  const isLoading = isCreatingGoal || isUpdatingGoal;

  const initialValues: GoalFormValues =
    isEdit && goal ? goalToFormValues(goal) : GOAL_FORM_INITIAL_VALUES;

  const handleSubmit = (values: GoalFormValues) => {
    const isBehaviorReduction = values.goal_type === "behavior-reduction";

    const payload: Partial<Goal> = {
      behavior_subject: values.behavior_subject as Goal["behavior_subject"],
      goal_type: values.goal_type as Goal["goal_type"],
      title: values.title.trim(),
      domain: values.domain as Goal["domain"],
      status: values.status,
      description: values.description.trim(),
      general_session_instructions: values.general_session_instructions || null,
      reinforcement_context: values.reinforcement_context || null,
      strategy_notes: values.strategy_notes || null,
      // Section 1.5 — only sent when goal type is behavior-reduction
      operational_definition: isBehaviorReduction ? values.operational_definition.trim() : null,
      hypothesized_functions: isBehaviorReduction ? values.hypothesized_functions : [],
      common_antecedents: isBehaviorReduction ? values.common_antecedents.trim() : null,
      mastery_definition: values.mastery_definition.trim(),
      generalization_definition: values.generalization_definition.trim(),
      resources: values.resources.length > 0 ? values.resources : null,
    };

    if (isEdit && goal) {
      // Backend rejects status changes via PUT unless a rationale is supplied.
      if (values.status !== goal.status) {
        payload.rationale = STATUS_CHANGE_RATIONALE_DEFAULT;
      }
      updateGoalAction({ goalId: goal.id, clientId, goalData: payload });
    } else {
      createGoalAction({
        clientId,
        // the API required a datetime ISO
        goalData: { ...payload, start_date: new Date().toISOString() },
      });
    }
  };

  return (
    <Formik
      key={open ? `${mode}-${goal?.id ?? "new"}` : "closed"}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnMount
    >
      {(formik) => {
        const handleCloseAttempt = (
          _event?: unknown,
          reason?: "backdropClick" | "escapeKeyDown"
        ) => {
          if (reason === "backdropClick") {
            return;
          }

          if (formik.dirty) {
            setDiscardOpen(true);
          } else {
            onClose();
          }
        };

        const handleDiscardConfirm = () => {
          setDiscardOpen(false);
          formik.resetForm();
          onClose();
        };

        return (
          <>
            <Dialog
              open={open}
              onClose={isLoading ? undefined : handleCloseAttempt}
              maxWidth="sm"
              fullWidth
              scroll="paper"
            >
              {/* Title row */}
              <DialogTitle
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  px: 7,
                  py: 6,
                }}
              >
                <Stack component="p" margin={0}>
                  <Typography component="span" variant="h4" fontWeight={500}>
                    {isEdit ? "Edit Goal" : "Create Goal"}
                  </Typography>
                  <Typography component="span" variant="body2" color="text.secondary">
                    Goals define where we&apos;re going. Targets define how we get there.
                  </Typography>
                </Stack>
                <IconButton
                  size="small"
                  aria-label="Close dialog"
                  onClick={handleCloseAttempt}
                  disabled={isLoading}
                  sx={{ color: "text.secondary", mt: 1 }}
                >
                  <X size={20} />
                </IconButton>
              </DialogTitle>

              {/* Scrollable content */}
              <DialogContent sx={{ px: 7, py: 3 }}>
                <Stack gap={4}>
                  <GoalFormSection1 />
                  {formik.values.goal_type === "behavior-reduction" && <GoalFormSection15 />}
                  <GoalFormSection2 />
                </Stack>
              </DialogContent>

              {/* Footer actions */}
              <DialogActions sx={{ px: 7, py: 5, gap: 2 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => formik.resetForm()}
                  disabled={isLoading}
                  aria-label={isEdit ? "Reset changes" : "Reset form"}
                >
                  Reset
                </Button>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  loading={isLoading}
                  disabled={isLoading || !formik.isValid || (isEdit ? !formik.dirty : false)}
                  onClick={() => formik.submitForm()}
                  aria-label={isEdit ? "Save changes" : "Create goal"}
                >
                  {isEdit ? "Save Changes" : "Create Goal"}
                </LoadingButton>
              </DialogActions>
            </Dialog>

            {/* Discard confirmation — shown when closing with unsaved changes */}
            <ConfirmDialog
              open={discardOpen}
              title="Discard unsaved changes?"
              confirmLabel="Discard"
              cancelLabel="Keep Editing"
              onClose={() => setDiscardOpen(false)}
              onCancel={() => setDiscardOpen(false)}
              onConfirm={handleDiscardConfirm}
            >
              <Typography variant="body1" color="text.secondary" sx={{ py: 3 }}>
                You have unsaved changes that will be lost if you close this panel.
              </Typography>
            </ConfirmDialog>
          </>
        );
      }}
    </Formik>
  );
};

export default GoalBuilderPanel;
